import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useUpdatePayment } from "@/api/payments";
import { currencies } from "@/lib/constants";
import { useChapters } from "@/api/chapters";
import PropTypes from "prop-types";

const CURRENCY_OPTIONS = ["INR", "AED", "SAR", "KWD", "BHD", "OMR", "QAR"];

/**
 * payment prop shape (from finance page):
 *   _id, paymentId, amount, sponsorshipId, type ("Direct"|"Mahallu"|"Individual"),
 *   sponsorName, sponsorPhone, place, mahalluName, currency
 *
 * isSponsored = type !== "Direct"
 * For sponsored: name, place, phone, amount editable; currency locked (belongs to sponsorship).
 * For direct: all fields including currency are editable.
 */
export function EditPaymentModal({ isOpen, onClose, payment }) {
  const { mutate: updatePayment, isPending } = useUpdatePayment();
  const { data: chapters = [] } = useChapters();

  const isSponsored = payment?.type && payment.type !== "Direct";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    place: "",
    amount: "",
    currency: "INR",
    careOfName: "",
    careOfPhone: "",
    chapter: "none",
  });

  useEffect(() => {
    if (payment) {
      setForm({
        name: payment.sponsorName || "",
        phone: payment.sponsorPhone || "",
        place: payment.place || payment.mahalluName || "",
        amount: String(payment.amount || ""),
        currency: payment.currency || "INR",
        careOfName: payment.careOf?.name || "",
        careOfPhone: payment.careOf?.phone || "",
        chapter: payment.chapter?._id || payment.chapter || "none",
      });
    }
  }, [payment]);

  if (!payment) return null;

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSelectChange = (field) => (value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return;

    const payload = isSponsored
      ? {
          id: payment._id,
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          place: form.place.trim(),
          amount,
          careOf: {
            name: form.careOfName.trim() || undefined,
            phone: form.careOfPhone.trim() || undefined,
          },
          chapter:
            form.chapter && form.chapter !== "none" ? form.chapter : undefined,
        }
      : {
          id: payment._id,
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          place: form.place.trim(),
          amount,
          currency: form.currency,
          careOf: {
            name: form.careOfName.trim() || undefined,
            phone: form.careOfPhone.trim() || undefined,
          },
          chapter:
            form.chapter && form.chapter !== "none" ? form.chapter : undefined,
        };

    updatePayment(payload, {
      onSuccess: () => onClose(),
    });
  };

  const isValid =
    form.amount &&
    Number(form.amount) > 0 &&
    form.name.trim() &&
    form.place.trim();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Payment</DialogTitle>
          <DialogDescription>
            Payment <span className="font-semibold">#{payment.paymentId}</span>
            {isSponsored && (
              <>
                {" "}
                · Linked to{" "}
                <span className="font-semibold">{payment.sponsorshipId}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {isSponsored && (
            <div className="rounded-lg border p-3 bg-muted/50 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sponsorship</span>
                <span className="font-medium">{payment.sponsorshipId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="font-medium">{payment.type}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Currency</span>
                <span className="font-medium">
                  {payment.currency || "INR"} — locked to sponsorship
                </span>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Donor name"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Phone (optional)</Label>
            <Input
              value={form.phone}
              onChange={handleChange("phone")}
              placeholder="+91XXXXXXXXXX"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Place</Label>
            <Input
              value={form.place}
              onChange={handleChange("place")}
              placeholder="Place"
            />
          </div>

          {/* Care Of Fields */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>C/O Name (optional)</Label>
              <Input
                value={form.careOfName}
                onChange={handleChange("careOfName")}
                placeholder="Care of name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>C/O Phone (optional)</Label>
              <Input
                value={form.careOfPhone}
                onChange={handleChange("careOfPhone")}
                placeholder="Care of phone"
              />
            </div>
          </div>

          {/* Chapter Selection */}
          <div className="space-y-1.5">
            <Label>Chapter (optional)</Label>
            <Select
              value={form.chapter}
              onValueChange={handleSelectChange("chapter")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select chapter..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {chapters.map((ch) => (
                  <SelectItem key={ch._id} value={ch._id}>
                    {ch.chapterName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input
                type="number"
                min={1}
                value={form.amount}
                onChange={handleChange("amount")}
                placeholder="Amount"
              />
              {isSponsored && (
                <p className="text-xs text-muted-foreground">
                  Changing amount updates sponsorship total.
                </p>
              )}
            </div>

            {!isSponsored && (
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select
                  value={form.currency}
                  onValueChange={handleSelectChange("currency")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c} ({currencies[c]?.symbol || c})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isPending || !isValid}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

EditPaymentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  payment: PropTypes.object,
};
